import { memo, useCallback } from 'react';

import { type Board } from '@air/api/types';
import { DownloadIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';

import { BoardStatsBar } from '~/_components/BoardStatsBar';
import { ToggleNestedItemsButton } from '~/_components/ToggleNestedItemsButton';
import { useFormatBoardStats } from '~/_hooks/useFormatBoardStats';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { usePublicBoardStats } from '~/components/v2/hooks/queries/usePublicBoardsStats';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useDownloadPublicBoard } from '~/hooks/useDownloadPublicBoard';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export type PublicBoardStatsBarProps = {
  boardId: Board['id'];
};

export const PublicBoardStatsBar = memo(({ boardId }: PublicBoardStatsBarProps) => {
  const { permissions } = usePublicPermissions();
  const { downloadPublicBoard } = useDownloadPublicBoard();
  const { presentationSettings, shortId } = useShortIdContext();
  const hideDownloadAll = presentationSettings?.hideDownloadAll === true;
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const { isFlattenedView } = useIsFlattenedView({ disabled: isExpandNestedDisabled });
  const { data } = usePublicBoardStats({
    boardId,
    includeDescendants: isFlattenedView,
    shortId,
  });
  const { formatBoardStats } = useFormatBoardStats();
  const { isSearchActive } = useIsSearchActive();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { ui2026 } = useTemporaryFeatureFlags();

  const isEmptyBoard = data?.assetCount === 0 && data?.fileCount === 0 && data?.sizeBytes === 0;

  const isDownloadVisible = canGenerateZip(permissions) && !!boardId && !hideDownloadAll;

  const onDownloadClick = useCallback(() => {
    if (boardId) {
      downloadPublicBoard({ boardId, trackLocation: 'board-stats-bar' });
    }
  }, [boardId, downloadPublicBoard]);

  return !isEmptyBoard && data ? (
    <BoardStatsBar
      actions={
        !isSearchActive && (
          <>
            {isAboveMediumScreen && !ui2026 && !isExpandNestedDisabled && (
              <>
                <ToggleNestedItemsButton />
                {isDownloadVisible && <div className="h-8 w-px bg-gray-4" />}
              </>
            )}

            {isDownloadVisible && (
              <Tooltip label="Download board">
                <IconButton
                  adsIcon
                  appearance="ghost"
                  color="gray"
                  icon={DownloadIcon}
                  label="Download board"
                  onClick={onDownloadClick}
                />
              </Tooltip>
            )}
          </>
        )
      }
      stats={formatBoardStats(data)}
    />
  ) : null;
});

PublicBoardStatsBar.displayName = 'PublicBoardStatsBar';
